import React, { useState, useEffect } from "react";
import { 
  Globe, 
  Search, 
  Target, 
  AlertCircle, 
  CheckCircle, 
  Save,
  Code,
  Link
} from "lucide-react";
import { PageWrapper } from "components/ui/PageWrapper";
import { GetAllPagesWithSEO, GetPageSEO, SavePageSEO } from "rest/seo";
import Swal from 'sweetalert2';

export default function SEOSettings() {
  const [selectedPage, setSelectedPage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [seoData, setSeoData] = useState({
    seo_title: "",
    meta_description: "",
    keywords: "",
    og_title: "",
    og_description: "",
    og_image: "",
    canonical_url: "",
    meta_robots: "index,follow",
    structured_data: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ogImageFile, setOgImageFile] = useState(null);

  // Real API calls
  const { data: pagesData, isLoading: pagesLoading, error: pagesError, refetch: refetchPages } = GetAllPagesWithSEO();
  const { data: pageSeoData, isLoading: seoLoading, refetch: refetchSeo } = GetPageSEO(selectedPage);
  const saveSeoMutation = SavePageSEO();

  // Extract pages from API response
  const pages = pagesData?.data || [];

  // Update SEO data when page selection changes or when SEO data is fetched
  useEffect(() => {
    if (selectedPage && pageSeoData?.data) {
      const seo = pageSeoData.data;
      setSeoData({
        seo_title: seo.seo_title || "",
        meta_description: seo.meta_description || "",
        keywords: seo.keywords || "",
        og_title: seo.og_title || "",
        og_description: seo.og_description || "",
        og_image: seo.og_image || "",
        canonical_url: seo.canonical_url || "",
        meta_robots: seo.meta_robots || "index,follow",
        structured_data: seo.structured_data || ""
      });
      setOgImageFile(null);
      setErrors({}); // Clear errors when loading new data
    } else if (selectedPage) {
      // Reset form for new page selection
      setSeoData({
        seo_title: "",
        meta_description: "",
        keywords: "",
        og_title: "",
        og_description: "",
        og_image: "",
        canonical_url: "",
        meta_robots: "index,follow",
        structured_data: ""
      });
      setErrors({});
    }
  }, [selectedPage, pageSeoData]);

  // Filter pages based on search term
  const filteredPages = pages.filter(page => 
    page.name && page.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = {};
    
    if (!seoData.seo_title.trim()) {
      newErrors.seo_title = "Page Title is required";
    } else if (seoData.seo_title.length > 60) {
      newErrors.seo_title = "Page Title must be 60 characters or less";
    }
    
    if (!seoData.meta_description.trim()) {
      newErrors.meta_description = "Meta Description is required";
    } else if (seoData.meta_description.length > 160) {
      newErrors.meta_description = "Meta Description must be 160 characters or less";
    }
    
    if (seoData.keywords && seoData.keywords.length > 500) {
      newErrors.keywords = "Keywords must be 500 characters or less";
    }
    
    if (seoData.og_title && seoData.og_title.length > 60) {
      newErrors.og_title = "OG Title must be 60 characters or less";
    }
    
    if (seoData.og_description && seoData.og_description.length > 160) {
      newErrors.og_description = "OG Description must be 160 characters or less";
    }
    
    if (seoData.canonical_url && !isValidUrl(seoData.canonical_url)) {
      newErrors.canonical_url = "Please enter a valid URL";
    }
    
    if (seoData.og_image && !isValidUrl(seoData.og_image)) {
      newErrors.og_image = "Please enter a valid image URL or upload an image";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      // Accept normal URLs and data URLs (base64) created by file uploads
      if (typeof string === 'string' && string.startsWith('data:')) return true;
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handle OG image file uploads by converting to base64 data URL
  const handleOgImageUpload = (file) => {
    if (!file) return;
    setOgImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      setSeoData(prev => ({ ...prev, og_image: result }));
      // Clear any existing error for og_image
      setErrors(prev => ({ ...prev, og_image: "" }));
    };
    reader.readAsDataURL(file);
  };

  const removeOgImage = () => {
    setSeoData(prev => ({ ...prev, og_image: "" }));
    setOgImageFile(null);
  };

  const handlePageSelect = (pageId) => {
    setSelectedPage(pageId);
  };

  const handleSaveSEO = async () => {
    if (!selectedPage) {
      Swal.fire({
        icon: 'warning',
        title: 'No Page Selected',
        text: 'Please select a page first to save SEO settings.',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // If a file was selected, send multipart/form-data with key 'og_image_file'
      if (ogImageFile) {
        const form = new FormData();
        form.append('page_id', selectedPage);
        form.append('seo_title', seoData.seo_title);
        form.append('meta_description', seoData.meta_description);
        form.append('keywords', seoData.keywords);
        form.append('og_title', seoData.og_title);
        form.append('og_description', seoData.og_description);
        form.append('canonical_url', seoData.canonical_url);
        form.append('meta_robots', seoData.meta_robots);
        form.append('structured_data', seoData.structured_data);
        form.append('og_image_file', ogImageFile);

        await saveSeoMutation.mutateAsync(form);
      } else {
        await saveSeoMutation.mutateAsync({
          page_id: selectedPage,
          seo_title: seoData.seo_title,
          meta_description: seoData.meta_description,
          keywords: seoData.keywords,
          og_title: seoData.og_title,
          og_description: seoData.og_description,
          og_image: seoData.og_image,
          canonical_url: seoData.canonical_url,
          meta_robots: seoData.meta_robots,
          structured_data: seoData.structured_data
        });
      }

      Swal.fire({
        icon: 'success',
        title: 'SEO Settings Saved',
        text: 'SEO settings for this page have been saved successfully!',
        confirmButtonText: 'OK'
      });
      refetchSeo(); // Refresh the SEO data
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error Saving SEO',
        text: `Error saving SEO settings: ${error.message || 'Unknown error'}`,
        confirmButtonText: 'OK'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSeoData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  if (pagesLoading) {
    return (
      <div className="seo_settings">
        <PageWrapper slug="seo-settings" name="Individual Page SEO" />
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading pages from database...</p>
          </div>
        </div>
      </div>
    );
  }

  if (pagesError) {
    return (
      <div className="seo_settings">
        <PageWrapper slug="seo-settings" name="Individual Page SEO" />
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Pages</h4>
            <p>Failed to load pages from database. Please try again later.</p>
            <hr />
            <p className="mb-0">{pagesError.message}</p>
            <button 
              className="btn btn-outline-danger mt-3"
              onClick={() => refetchPages()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seo_settings">
      <PageWrapper slug="seo-settings" name="Individual Page SEO" />
      
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1">Individual Page SEO Settings</h4>
                <p className="text-muted mb-0">
                  Optimize SEO settings for individual pages
                </p>
              </div>
              {selectedPage && (
                <button
                  className="btn btn-primary"
                  onClick={handleSaveSEO}
                  disabled={isSubmitting || saveSeoMutation.isPending}
                >
                  {isSubmitting || saveSeoMutation.isPending ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-2" />
                      Save SEO Settings
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Pages List */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">
                  <Globe size={16} className="me-2" />
                  Select Page
                </h6>
              </div>
              <div className="card-body p-0">
                {/* Search */}
                <div className="p-3 border-bottom">
                  <div className="input-group">
                    <span className="input-group-text">
                      <Search size={16} />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search pages..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Pages List */}
                <div className="list-group list-group-flush" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {filteredPages.length === 0 ? (
                    <div className="p-3 text-center text-muted">
                      {searchTerm ? 'No pages found matching your search' : 'No pages found in database'}
                    </div>
                  ) : (
                    filteredPages.map((page) => (
                      <button
                        key={page.id}
                        className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                          selectedPage === page.id ? 'active' : ''
                        }`}
                        onClick={() => handlePageSelect(page.id)}
                      >
                        <div className="d-flex flex-column">
                          <strong className="text-start">{page.name}</strong>
                          <small className="text-start text-muted">
                            {page.slug || page.link}
                          </small>
                        </div>
                        {page.seo_settings && (
                          <CheckCircle size={16} className="text-success" title="SEO configured" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SEO Form */}
          <div className="col-md-8">
            {!selectedPage ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <Globe size={48} className="text-muted mb-3" />
                  <h5>Select a Page</h5>
                  <p className="text-muted">
                    Choose a page from the list to configure its SEO settings
                  </p>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">
                    <Target size={16} className="me-2" />
                    SEO Settings for: {pages.find(p => p.id === selectedPage)?.name}
                  </h6>
                </div>
                <div className="card-body">
                  {seoLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3">Loading SEO settings...</p>
                    </div>
                  ) : (
                    <>
                      {/* Basic SEO */}
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              <Search className="me-1" size={16} />
                              Page Title *
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors.seo_title ? 'is-invalid' : ''}`}
                              value={seoData.seo_title}
                              onChange={(e) => handleInputChange('seo_title', e.target.value)}
                              placeholder="Enter page title for search engines..."
                              maxLength={60}
                            />
                            <div className="form-text">
                              {seoData.seo_title.length}/60 characters
                            </div>
                            {errors.seo_title && (
                              <div className="invalid-feedback">{errors.seo_title}</div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              <Target className="me-1" size={16} />
                              Meta Description *
                            </label>
                            <textarea
                              className={`form-control ${errors.meta_description ? 'is-invalid' : ''}`}
                              rows="3"
                              value={seoData.meta_description}
                              onChange={(e) => handleInputChange('meta_description', e.target.value)}
                              placeholder="Enter meta description for search results..."
                              maxLength={160}
                            />
                            <div className="form-text">
                              {seoData.meta_description.length}/160 characters
                            </div>
                            {errors.meta_description && (
                              <div className="invalid-feedback">{errors.meta_description}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          <AlertCircle className="me-1" size={16} />
                          Keywords
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.keywords ? 'is-invalid' : ''}`}
                          value={seoData.keywords}
                          onChange={(e) => handleInputChange('keywords', e.target.value)}
                          placeholder="Enter keywords separated by commas..."
                          maxLength={500}
                        />
                        <div className="form-text">
                          Separate keywords with commas (e.g., seo, optimization, marketing)
                        </div>
                        {errors.keywords && (
                          <div className="invalid-feedback">{errors.keywords}</div>
                        )}
                      </div>

                      {/* Open Graph */}
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              <Globe className="me-1" size={16} />
                              OG Title
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors.og_title ? 'is-invalid' : ''}`}
                              value={seoData.og_title}
                              onChange={(e) => handleInputChange('og_title', e.target.value)}
                              placeholder="Open Graph title for social media..."
                              maxLength={60}
                            />
                            {errors.og_title && (
                              <div className="invalid-feedback">{errors.og_title}</div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              <Globe className="me-1" size={16} />
                              OG Image
                            </label>

                            <div className="mb-2">
                              <input
                                type="file"
                                accept="image/*"
                                className={`form-control ${errors.og_image ? 'is-invalid' : ''}`}
                                onChange={(e) => {
                                  const file = e.target.files && e.target.files[0];
                                  if (file) handleOgImageUpload(file);
                                }}
                              />
                            </div>

                            <div className="mb-2">
                              <small className="text-muted">Or paste an image URL below</small>
                            </div>

                            <input
                              type="text"
                              className={`form-control mb-2 ${errors.og_image ? 'is-invalid' : ''}`}
                              value={seoData.og_image && !seoData.og_image.startsWith('data:') ? seoData.og_image : ''}
                              onChange={(e) => handleInputChange('og_image', e.target.value)}
                              placeholder="https://example.com/image.jpg"
                            />

                            {seoData.og_image ? (
                              <div className="mt-2 d-flex align-items-center">
                                <img
                                  src={seoData.og_image}
                                  alt="OG Preview"
                                  style={{ maxWidth: 120, maxHeight: 80, objectFit: 'cover', borderRadius: 4 }}
                                />
                                <button className="btn btn-sm btn-outline-danger ms-3" onClick={removeOgImage} type="button">Remove</button>
                              </div>
                            ) : null}

                            {errors.og_image && (
                              <div className="invalid-feedback d-block">{errors.og_image}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          <Globe className="me-1" size={16} />
                          OG Description
                        </label>
                        <textarea
                          className={`form-control ${errors.og_description ? 'is-invalid' : ''}`}
                          rows="3"
                          value={seoData.og_description}
                          onChange={(e) => handleInputChange('og_description', e.target.value)}
                          placeholder="Open Graph description for social media..."
                          maxLength={160}
                        />
                        {errors.og_description && (
                          <div className="invalid-feedback">{errors.og_description}</div>
                        )}
                      </div>

                      {/* Advanced Settings */}
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              <Link className="me-1" size={16} />
                              Canonical URL
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors.canonical_url ? 'is-invalid' : ''}`}
                              value={seoData.canonical_url}
                              onChange={(e) => handleInputChange('canonical_url', e.target.value)}
                              placeholder="https://example.com/page"
                            />
                            {errors.canonical_url && (
                              <div className="invalid-feedback">{errors.canonical_url}</div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              <AlertCircle className="me-1" size={16} />
                              Meta Robots
                            </label>
                            <select
                              className="form-select"
                              value={seoData.meta_robots}
                              onChange={(e) => handleInputChange('meta_robots', e.target.value)}
                            >
                              <option value="index,follow">Index, Follow</option>
                              <option value="noindex,follow">No Index, Follow</option>
                              <option value="index,nofollow">Index, No Follow</option>
                              <option value="noindex,nofollow">No Index, No Follow</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          <Code className="me-1" size={16} />
                          Structured Data (JSON-LD)
                        </label>
                        <textarea
                          className="form-control"
                          rows="6"
                          value={seoData.structured_data}
                          onChange={(e) => handleInputChange('structured_data', e.target.value)}
                          placeholder="Enter JSON-LD structured data..."
                        />
                        <div className="form-text">
                          Enter valid JSON-LD structured data for better search engine understanding
                        </div>
                      </div>

                      {/* SEO Preview */}
                      <div className="mb-3">
                        <label className="form-label">
                          <Search className="me-1" size={16} />
                          Search Result Preview
                        </label>
                        <div className="search-preview">
                          {(() => {
                            const page = pages.find(p => p.id === selectedPage);
                            const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

                            // Resolve a friendly frontend URL for preview:
                            // - If page.link is an absolute URL and does NOT contain /admin/api/, use it.
                            // - If page.link contains /admin/api/, show the origin only (e.g., https://domain.com).
                            // - Otherwise, if slug exists, build from baseUrl + slug.
                            let fullUrl = baseUrl;
                            try {
                              if (page?.link) {
                                try {
                                  const parsed = new URL(page.link);
                                  if (/\/admin\/api\//i.test(parsed.pathname)) {
                                    fullUrl = parsed.origin;
                                  } else {
                                    fullUrl = page.link;
                                  }
                                } catch (e) {
                                  // page.link is not absolute — treat as path
                                  if ((page.link || '').includes('/admin/api/')) {
                                    try { fullUrl = (new URL(baseUrl)).origin; } catch { fullUrl = baseUrl; }
                                  } else {
                                    fullUrl = `${baseUrl}/${(page.link || '').replace(/^\/+/, '')}`;
                                  }
                                }
                              } else if (page?.slug) {
                                if ((page.slug || '').startsWith('http')) {
                                  try {
                                    const parsed = new URL(page.slug);
                                    if (/\/admin\/api\//i.test(parsed.pathname)) {
                                      fullUrl = parsed.origin;
                                    } else {
                                      fullUrl = page.slug;
                                    }
                                  } catch (e) {
                                    fullUrl = `${baseUrl}/${page.slug.replace(/^\/+/, '')}`;
                                  }
                                } else {
                                  fullUrl = `${baseUrl}/${page.slug.replace(/^\/+/, '')}`;
                                }
                              }
                            } catch (e) {
                              fullUrl = baseUrl;
                            }

                            // Normalize preview URL: remove any '/admin/api' segment and collapse duplicate slashes
                            try {
                              if (typeof fullUrl === 'string') {
                                const parts = fullUrl.split('://');
                                if (parts.length > 1) {
                                  parts[1] = parts[1].replace(/\/admin\/api/ig, '').replace(/\/{2,}/g, '/');
                                  fullUrl = parts[0] + '://' + parts[1];
                                } else {
                                  fullUrl = fullUrl.replace(/\/admin\/api/ig, '').replace(/\/{2,}/g, '/');
                                }
                              }
                            } catch (e) {
                              // ignore normalization errors
                            }

                            const title = seoData.seo_title || page?.name || 'Page Title';

                            return (
                              <div className="d-flex align-items-start">
                                {seoData.og_image ? (
                                  <img
                                    src={seoData.og_image}
                                    alt="OG Preview"
                                    style={{ width: 88, height: 88, objectFit: 'contain', background: '#fff', borderRadius: 6, marginRight: 12, padding: 6 }}
                                  />
                                ) : (
                                  <div style={{ width: 88, height: 88, backgroundColor: '#f5f5f5', borderRadius: 6, marginRight: 12 }} />
                                )}

                                <div style={{ flex: 1 }}>
                                  <div className="preview-title">
                                    <a href={fullUrl} target="_blank" rel="noreferrer" style={{ color: '#1a0dab', textDecoration: 'none' }}>
                                      {title}
                                    </a>
                                  </div>
                                  <div className="preview-url text-success" style={{ fontSize: 13 }}>
                                    {fullUrl}
                                  </div>
                                  <div className="preview-description" style={{ marginTop: 8 }}>
                                    {seoData.meta_description || 'Meta description will appear here...'}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
